import dayjs from "dayjs";
import "dayjs/locale/de";
import { GenCase, LLMCaseItem, LLMCasesMap } from "../models";
dayjs.locale("de");
const DATE_TIME_SECONDS_FORMAT = "DD. MMM YYYY HH:mm:ss";
const TIME_ONLY = "HH:mm:ss";

const DATE_TIME_FORMAT = "DD. MMM YYYY HH:mm";
const DATE_FORMAT = "DD. MMM YYYY ";

export function formatToDateTime(date: any, format = DATE_TIME_FORMAT, seconds = false): string {
  return dayjs(date).format(format);
}

export function formatToDate(date: any, format = DATE_FORMAT): string {
  return dayjs(date).format(format);
}

export function parseDateToTimestamp(date: string) {
  let timestamp;

  if (date.includes(" - ")) {
    const dates = date.split(" - ");

    const startDate = dayjs(dates[0], ["DD.MM.YYYY", "DD. MMMM YYYY"], true);
    const endDate = dayjs(dates[1], ["DD.MM.YYYY", "DD. MMMM YYYY"], true);

    if (startDate.isValid() && endDate.isValid()) {
      return {
        startTimestamp: startDate.unix(),
        endTimestamp: endDate.unix(),
      };
    }
  } else {
    const parsedDate = dayjs(date, ["DD.MM.YYYY", "DD. MMMM YYYY"], true);
    if (parsedDate.isValid()) {
      timestamp = parsedDate.unix();
    }
  }

  return timestamp || null;
}

export const humanizeTimeOnly = (timestamp: string | number) => {
  if (typeof timestamp === "string") {
    timestamp = parseInt(timestamp);
  }
  const date = new Date(timestamp);
  return formatToDateTime(date, TIME_ONLY);
};
export const humanizeTimestamp = (timestamp: string | number, seconds = false) => {
  if (typeof timestamp === "string") {
    timestamp = parseInt(timestamp);
  }
  const date = new Date(timestamp);
  return formatToDateTime(date, seconds === true ? DATE_TIME_SECONDS_FORMAT : DATE_TIME_FORMAT);
};
export const humanizeDate = (timestamp: string | number) => {
  if (typeof timestamp === "string") {
    timestamp = parseInt(timestamp);
  }
  const date = new Date(timestamp);
  return formatToDate(date);

  // return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};

export const groupItemsByDate = (items: GenCase[]): { [key: string]: LLMCaseItem[] } => {
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  startOfWeek.setHours(0, 0, 0, 0); // Start of the current week (Sunday)

  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  startOfMonth.setHours(0, 0, 0, 0); // Start of the current month

  const groupedItems: { [key: string]: LLMCaseItem[] } = {
    Heute: [],
    "Diese Woche": [],
    "Diesen Monat": [],
  };

  const now = today.getTime();

  for (let item of items) {
    const title = item.title || "";
    const hasWorkflow = /workflow/i.test(title);
    const hasDocuments = /chat mit/i.test(title);
    const caseItem: LLMCaseItem = {
      id: item.id,
      name: (title || "Case von " + humanizeTimestamp(item.creation_date)).slice(0, 100) || "",
      creation_date: item.creation_date,
      last_modified: item.last_modified,
      hasDocuments,
      hasWorkflow,
    };
    const creationDateConverted = new Date(item.creation_date);

    const creationDate = creationDateConverted.getTime();
    if (isSameDay(creationDateConverted, today)) {
      groupedItems["Heute"].push(caseItem);
    } else if (creationDate >= startOfWeek.getTime()) {
      groupedItems["Diese Woche"].push(caseItem);
    } else if (creationDate >= startOfMonth.getTime()) {
      groupedItems["Diesen Monat"].push(caseItem);
    } else {
      const monthYearKey = `${creationDateConverted.toLocaleString("de-at", {
        month: "long",
      })} ${creationDateConverted.getFullYear()}`;
      if (!groupedItems[monthYearKey]) {
        groupedItems[monthYearKey] = [];
      }
      groupedItems[monthYearKey].push(caseItem);
    }
  }
  for (const group in groupedItems) {
    if (groupedItems[group].length === 0) {
      delete groupedItems[group];
    }
  }

  return groupedItems;
};
function isSameDay(date1: Date, date2: Date): boolean {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
}

export const mergeLLMCasesByDate = (existingDocs: LLMCasesMap, newDocs: LLMCasesMap): LLMCasesMap => {
  Object.keys(newDocs).forEach((key) => {
    // If the key exists in the existing documents
    if (existingDocs.hasOwnProperty(key)) {
      existingDocs[key] = [...existingDocs[key], ...newDocs[key]];
    } else {
      existingDocs[key] = newDocs[key];
    }
  });

  return existingDocs;
};

export async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const relTimeFormatter = new Intl.RelativeTimeFormat("de-AT", { numeric: "auto" });
const absDateTimeFormatter = new Intl.DateTimeFormat("de-AT", { dateStyle: "short", timeStyle: "short" });
const absDateOnlyFormatter = new Intl.DateTimeFormat("de-AT", { dateStyle: "short" });

export const formatAbsoluteDate = (timestamp: string | number): string => {
  const ts = typeof timestamp === "string" ? parseInt(timestamp) : timestamp;
  return absDateTimeFormatter.format(new Date(ts));
};

export const formatRelativeDate = (timestamp: string | number): string => {
  const ts = typeof timestamp === "string" ? parseInt(timestamp) : timestamp;
  const diffMs = Date.now() - ts;
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 60) return "gerade eben";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return relTimeFormatter.format(-diffMin, "minute");
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return relTimeFormatter.format(-diffHr, "hour");
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return relTimeFormatter.format(-diffDay, "day");
  return absDateOnlyFormatter.format(new Date(ts));
};
